import { useRmxServiceApi } from '../api';
import { useServerDataTable } from '../../shared-compat/DataTable/hooks';
import { useModal } from 'src/portal/hooks/useModal';
import { RmxServiceSampleModal, type RmxServiceSampleModalParam } from '../RmxServiceSampleModal/RmxServiceSampleModal';
import React, { useMemo, useState } from 'react';
import { sampleColumnsDef } from '../RmxServiceTableDefinitions/SamplesTableDefinition';
import { DataTable } from '../../shared-compat/DataTable/DataTable';
import { DayFilter } from '../../utils/DayFilter/DayFilter';
import DarkSelect from '../../shared-compat/utils/DarkSelect/DarkSelect';
import { unReceivedSampleColumns } from '../RmxServiceTableDefinitions/UnReceivedSampleTableDefinition';

interface SampleTablesInterface {
  filterByAssetId?: number;
  filterBySiteId?: number;
  filterOutReviewed?: boolean;
  enableSampleDataTable?: boolean;
  enablePendingSamplesTable?: boolean;
}

export function useSampleTables(props?: SampleTablesInterface) {
  const api = useRmxServiceApi();
  const sampleModal = useModal<RmxServiceSampleModalParam>();
  const [sampleDateFilter, setSampleDateFilter] = useState<number | null | string>(null);

  const sampleColumns = useMemo(() => sampleColumnsDef(), []);
  const unreceivedColumns = useMemo(() => unReceivedSampleColumns(), []);

  const sampleDataTable = useServerDataTable({
    queryKey: ['samples', sampleDateFilter, props?.filterByAssetId, props?.filterBySiteId, props?.filterOutReviewed],
    columns: sampleColumns,
    initialSorting: [{ id: 'sampleDate', desc: true }],
    getRows: async (params) => {
      return await api.getSamples({
        ...params,
        assetId: props?.filterByAssetId ?? null,
        siteId: props?.filterBySiteId ?? null,
        daysFilter: sampleDateFilter,
        filterOutReviewed: props?.filterOutReviewed
      });
    },
    enabled: props?.enableSampleDataTable
    // initialSorting: [{ id: 'Recommendations', desc: false }]
  });

  const pendingSamplesTable = useServerDataTable({
    queryKey: ['pending-samples', sampleDateFilter],
    columns: unreceivedColumns,
    initialSorting: [{ id: 'scannedAt', desc: false }],
    getRows: async (params) => {
      return await api.getPendingSamples({
        ...params,
        assetId: props?.filterByAssetId ?? null,
        siteId: props?.filterBySiteId ?? null
      });
    },
    enabled: props?.enablePendingSamplesTable
  });

  return { sampleDataTable, pendingSamplesTable, sampleModal, sampleDateFilter, setSampleDateFilter };
}

export function SamplesTable(props: SampleTablesInterface) {
  const [sampleResultType, setSampleResultType] = useState<string>('received');

  const { sampleDataTable, pendingSamplesTable, sampleModal, sampleDateFilter, setSampleDateFilter } = useSampleTables({
    filterByAssetId: props.filterByAssetId,
    filterBySiteId: props.filterBySiteId,
    filterOutReviewed: props.filterOutReviewed ?? true,
    enablePendingSamplesTable: sampleResultType === 'unreceived'
  });
  const options = [
    { value: 'received', label: 'Received' },
    { value: 'unreceived', label: 'Unreceived / Unlinked' }
  ];
  return (
    <>
      <DarkSelect
        style={{ marginBottom: '10px' }}
        onChange={(value) => {
          setSampleResultType(value);
        }}
        options={options}
        value={sampleResultType}
      />
      {sampleResultType === 'received' ? (
        <DataTable
          negativeMarginsOnMobile={true}
          table={sampleDataTable}
          onRowClick={async (row) => await sampleModal.open({ resource: { sampleData: row } })}
          footer={<DayFilter value={sampleDateFilter} setGlobalDateFilter={setSampleDateFilter} />}
        />
      ) : (
        <DataTable
          negativeMarginsOnMobile={true}
          table={pendingSamplesTable}
          footer={<DayFilter value={sampleDateFilter} setGlobalDateFilter={setSampleDateFilter} />}
        />
      )}
      {sampleModal.isOpen && <RmxServiceSampleModal state={sampleModal} refetchSampleHistory={sampleDataTable.refetch} />}
    </>
  );
}
